import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useBusinessStore } from '../store/useBusinessStore';
import { useAuthStore } from '../store/useAuthStore';
import { supabase } from '../supabaseClient';

const DEMO_BUSINESS_IDS = ['9001', '9002', '9003'];

export const isMissingCashDay = (error) =>
  ['PGRST202', '42883', '42P01', 'PGRST205'].includes(error?.code) || /cash_day_summary|cash_closings|close_cash_day/i.test(error?.message || '');

const explain = (error, fallback) => {
  if (error?.code === '42501') return 'No tienes permiso sobre la contabilidad de este negocio (solo el dueño o un administrador).';
  return error?.code === '22023' && error?.message ? error.message : fallback;
};

// Resumen en vivo de un día, cierre y reapertura. Las cifras y las reglas viven en la base (20261009a_orders_accounting_bridge.sql).
export const useCashDay = (day) => {
  const { activeBusinessId } = useBusinessStore();
  const { userRole } = useAuthStore();
  const isReadOnlyDemo = DEMO_BUSINESS_IDS.includes(String(activeBusinessId)) && userRole !== 'master';
  const [summary, setSummary] = useState(null);
  const [closings, setClosings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState(false);

  const load = useCallback(async () => {
    if (!activeBusinessId || !day) { setLoading(false); return; }
    setLoading(true);
    const business = String(activeBusinessId);
    const [s, c] = await Promise.all([
      supabase.rpc('cash_day_summary', { p_business: business, p_date: day }),
      supabase.from('cash_closings').select('id, closing_date, expected_cash, counted_cash, difference, closed_by, closed_at, notes, voided_at, void_reason')
        .eq('business_id', business).order('closing_date', { ascending: false }).order('closed_at', { ascending: false }).limit(30)
    ]);
    if (s.error) {
      const gone = isMissingCashDay(s.error);
      setMissing(gone);
      if (!gone) toast.error(explain(s.error, 'No se pudo leer el resumen del día.'));
      setSummary(null); setClosings([]);
    } else {
      setMissing(false);
      setSummary(s.data);
      setClosings(c.error ? [] : (c.data || []));
    }
    setLoading(false);
  }, [activeBusinessId, day]);

  useEffect(() => { load(); }, [load]);

  const guardDemo = () => {
    if (isReadOnlyDemo) { toast.error('Modo solo lectura: las cuentas demo no se pueden modificar.'); return true; }
    return false;
  };

  const closeDay = async (counted, notes) => {
    if (guardDemo()) return false;
    const { error } = await supabase.rpc('close_cash_day', { p_business: String(activeBusinessId), p_date: day, p_counted: Number(counted), p_notes: String(notes ?? '').trim() || null });
    if (error) { toast.error(explain(error, 'No se pudo cerrar el día.')); return false; }
    toast.success('Día cerrado');
    await load();
    return true;
  };

  const reopenDay = async (closingId, reason) => {
    if (guardDemo()) return false;
    const { error } = await supabase.rpc('reopen_cash_day', { p_closing: closingId, p_reason: String(reason ?? '').trim() });
    if (error) { toast.error(explain(error, 'No se pudo reabrir el día.')); return false; }
    toast.success('Día reabierto');
    await load();
    return true;
  };

  return { summary, closings, loading, missing, isReadOnlyDemo, reload: load, closeDay, reopenDay };
};
